import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { db } from '../../firebase-config';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';

function localDayBoundsMs() {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { startMs: start.getTime(), endMs: end.getTime() };
}

function readingTimeMs(data, docId) {
  const ts = data.timestamp;
  if (ts && typeof ts.toDate === 'function') {
    const t = ts.toDate().getTime();
    if (!Number.isNaN(t)) return t;
  }
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'string') {
    const t = new Date(ts).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const m = String(docId || '').match(/^(\d{4})(\d{2})(\d{2})_/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0).getTime();
  }
  return null;
}

function readingLatLng(data) {
  const loc = data.location;
  if (loc != null) {
    if (typeof loc.latitude === 'number' && typeof loc.longitude === 'number') {
      if (loc.latitude === 0 && loc.longitude === 0) return null;
      return { lat: loc.latitude, lng: loc.longitude };
    }
    if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
      if (loc.lat === 0 && loc.lng === 0) return null;
      return { lat: loc.lat, lng: loc.lng };
    }
  }
  const rc = data.received_coordinates;
  if (rc != null && rc.lat != null && rc.lng != null) {
    const lat = typeof rc.lat === 'number' ? rc.lat : parseFloat(rc.lat);
    const lng = typeof rc.lng === 'number' ? rc.lng : parseFloat(rc.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
      return { lat, lng };
    }
  }
  return null;
}

/**
 * Fetch device movement history from Firestore.
 * History is stored at: vehicles/{parentVehicleType}/vehicles/{deviceId}/history/{timestamp}
 */
export const fetchDeviceHistory = createAsyncThunk(
  'deviceHistory/fetch',
  async ({ deviceId, parentVehicleType, maxPoints = 50 }, { rejectWithValue }) => {
    try {
      if (!deviceId || !parentVehicleType) {
        return rejectWithValue('Missing deviceId or parentVehicleType');
      }

      const historyRef = collection(
        db,
        'vehicles',
        parentVehicleType,
        'vehicles',
        deviceId,
        'history'
      );

      const q = query(historyRef, orderBy('history_saved_at', 'desc'), limit(maxPoints));
      const snapshot = await getDocs(q);

      const points = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        const lat = data.latitude || data.location?.latitude;
        const lng = data.longitude || data.location?.longitude;
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
          points.push({
            lat: parseFloat(lat),
            lng: parseFloat(lng),
            timestamp: data.history_saved_at?.seconds
              ? data.history_saved_at.seconds * 1000
              : null,
            status: data.status,
          });
        }
      });

      // Reverse so oldest is first (for drawing the path chronologically)
      points.reverse();

      return { deviceId, points };
    } catch (error) {
      console.error('Error fetching device history:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * LG Band / Adafruit: today's GPS trail from adafruit/{docId}/readings (imported by adafruit_script).
 */
export const fetchLgBandTodayReadingsHistory = createAsyncThunk(
  'deviceHistory/fetchLgBandTodayReadings',
  async ({ mapDeviceId, adafruitFirestoreDocId }, { rejectWithValue }) => {
    try {
      if (!mapDeviceId || adafruitFirestoreDocId == null || adafruitFirestoreDocId === '') {
        return rejectWithValue('Missing LG Band map id or Adafruit document id');
      }
      const docId = String(adafruitFirestoreDocId);
      const { startMs, endMs } = localDayBoundsMs();

      const readingsRef = collection(db, 'adafruit', docId, 'readings');
      const q = query(readingsRef, orderBy('timestamp', 'desc'), limit(2500));
      const snapshot = await getDocs(q);

      const points = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.gps_status === 'no_data') return;
        if (data.coordinates_valid === false) return;

        const millis = readingTimeMs(data, docSnap.id);
        if (millis == null || Number.isNaN(millis) || millis < startMs || millis > endMs) return;

        const ll = readingLatLng(data);
        if (!ll) return;

        points.push({
          lat: ll.lat,
          lng: ll.lng,
          timestamp: millis,
          status: data.message_type || data.message_identifier || null,
        });
      });

      points.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

      return { deviceId: mapDeviceId, points };
    } catch (error) {
      console.error('Error fetching LG Band readings history:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  activeDeviceId: null,
  points: [],
  trailMode: 'none', // none | history | live
  liveSessionActive: false,
  liveSessionDeviceId: null,
  liveSessionDocId: null,
  liveSessionStartedAtMs: null,
  loading: false,
  error: null,
};

const deviceHistorySlice = createSlice({
  name: 'deviceHistory',
  initialState,
  reducers: {
    clearHistory: (state) => {
      state.activeDeviceId = null;
      state.points = [];
      state.trailMode = 'none';
      state.liveSessionActive = false;
      state.liveSessionDeviceId = null;
      state.liveSessionDocId = null;
      state.liveSessionStartedAtMs = null;
      state.error = null;
    },
    startLiveSession: (state, action) => {
      const {
        deviceId,
        adafruitFirestoreDocId = null,
        startedAtMs = Date.now(),
        initialPoint = null,
      } = action.payload || {};
      if (!deviceId) {
        return;
      }
      state.activeDeviceId = deviceId;
      state.points = [];
      state.trailMode = 'live';
      state.liveSessionActive = true;
      state.liveSessionDeviceId = deviceId;
      state.liveSessionDocId = adafruitFirestoreDocId != null ? String(adafruitFirestoreDocId) : null;
      state.liveSessionStartedAtMs = startedAtMs;
      state.error = null;
      if (initialPoint && Number.isFinite(initialPoint.lat) && Number.isFinite(initialPoint.lng)) {
        state.points.push(initialPoint);
      }
    },
    appendLivePoint: (state, action) => {
      if (state.trailMode !== 'live' || !state.liveSessionActive) return;
      const point = action.payload;
      if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return;
      if (state.liveSessionDeviceId && point.deviceId && point.deviceId !== state.liveSessionDeviceId) return;

      // Skip exact duplicates from snapshot replays.
      const duplicate = state.points.some((p) =>
        p.timestamp === point.timestamp &&
        p.lat === point.lat &&
        p.lng === point.lng
      );
      if (duplicate) return;

      state.points.push({
        lat: point.lat,
        lng: point.lng,
        timestamp: point.timestamp ?? Date.now(),
        status: point.status ?? null,
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeviceHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.activeDeviceId = action.payload.deviceId;
        state.points = action.payload.points;
        state.trailMode = 'history';
        state.liveSessionActive = false;
        state.liveSessionDeviceId = null;
        state.liveSessionDocId = null;
        state.liveSessionStartedAtMs = null;
      })
      .addCase(fetchDeviceHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchLgBandTodayReadingsHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLgBandTodayReadingsHistory.fulfilled, (state, action) => {
        state.loading = false;
        const pts = action.payload.points || [];
        if (pts.length === 0) {
          state.activeDeviceId = null;
          state.points = [];
          state.trailMode = 'none';
        } else {
          state.activeDeviceId = action.payload.deviceId;
          state.points = pts;
          state.trailMode = 'history';
        }
        state.liveSessionActive = false;
        state.liveSessionDeviceId = null;
        state.liveSessionDocId = null;
        state.liveSessionStartedAtMs = null;
        state.error = null;
      })
      .addCase(fetchLgBandTodayReadingsHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearHistory, startLiveSession, appendLivePoint } = deviceHistorySlice.actions;
export default deviceHistorySlice.reducer;
